import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import {
    getElectronScreenshareSources,
    filterScreenShareOptions,
} from 'flow/utils/electron-utils';
import { toggleSenderScreenShare } from 'flow/actions/meeting-actions';
import { useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import Tabs, { TabProps } from 'flow/components/ui-components/Tabs';

interface ElectronScreenSource {
    id: string;
    name: string;
    thumbnail: {
        toDataURL: () => string;
    };
}

interface FilteredScreenSources {
    allScreens: ElectronScreenSource[];
    allWindows: ElectronScreenSource[];
}

interface MeetingScreenSharePromptElectronProps {
    closeScreenShareMenu: () => void;
}

interface TabPanelProps {
    selectedTab: string;
    screenSources: ElectronScreenSource[];
    windowSources: ElectronScreenSource[];
    onStartScreenShare: (source: ElectronScreenSource) => void;
    i18next: any;
}

const TabPanel: React.FC<TabPanelProps> = ({
    selectedTab,
    screenSources,
    windowSources,
    onStartScreenShare,
    i18next,
}) => {
    const renderSources = (
        sources: ElectronScreenSource[],
        noSourcesKey: string,
    ) => (
        <div
            className={twMerge(
                // p-1 leaves room for the focus ring of the sources at the
                // scroller's edges.
                'flex max-h-[350px] flex-wrap items-center justify-around overflow-y-auto p-1',
            )}
        >
            {sources.length > 0 ? (
                sources.map((source: ElectronScreenSource) => (
                    <div key={source.id}>
                        <button
                            type="button"
                            className={twMerge(
                                'h-[140px] cursor-pointer border-none bg-transparent p-0 text-sm transition-opacity hover:opacity-80',
                            )}
                            onClick={() => onStartScreenShare(source)}
                        >
                            {source?.thumbnail && (
                                <img
                                    src={source.thumbnail.toDataURL()}
                                    alt={i18next.t(
                                        'videoconference.electron.screen_preview',
                                        {
                                            screenName: source.name,
                                        },
                                    )}
                                    className={twMerge(
                                        'h-full w-full rounded object-cover',
                                    )}
                                />
                            )}
                        </button>
                        <div
                            className={twMerge(
                                'mb-1.5 flex max-w-[180px] justify-center overflow-hidden text-ellipsis whitespace-nowrap',
                            )}
                        >
                            {source.name}
                        </div>
                    </div>
                ))
            ) : (
                <div className={twMerge('h-[140px] text-sm')}>
                    {i18next.t(noSourcesKey)}
                </div>
            )}
        </div>
    );

    return (
        <div>
            {selectedTab === 'screens' && (
                <>
                    <div className={twMerge('mb-3 text-xl font-medium')}>
                        {i18next.t(
                            'videoconference.electron.choose.screen.to.share',
                        )}
                    </div>
                    {renderSources(
                        screenSources,
                        'videoconference.electron.no.screen.to.share',
                    )}
                </>
            )}
            {selectedTab === 'windows' && (
                <>
                    <div className={twMerge('mb-3 text-xl font-medium')}>
                        {i18next.t(
                            'videoconference.electron.choose.window.to.share',
                        )}
                    </div>
                    {renderSources(
                        windowSources,
                        'videoconference.electron.no.window.to.share',
                    )}
                </>
            )}
        </div>
    );
};

const MeetingScreenSharePromptElectron: React.FC<
    MeetingScreenSharePromptElectronProps
> = ({ closeScreenShareMenu }) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const [screenSources, setScreenSources] = useState<ElectronScreenSource[]>(
        [],
    );
    const [windowSources, setWindowSources] = useState<ElectronScreenSource[]>(
        [],
    );
    const [selectedTab, setSelectedTab] = useState<string>('screens');

    useEffect(() => {
        const getElectronSources = async (): Promise<void> => {
            const validSources = await getElectronScreenshareSources();
            if (validSources && validSources.length > 0) {
                const sortedValidSources: FilteredScreenSources =
                    filterScreenShareOptions(validSources);
                const { allScreens, allWindows } = sortedValidSources;
                setScreenSources(allScreens);
                setWindowSources(allWindows);
            }
        };
        getElectronSources();
    }, []);

    const startScreenShare = (source: ElectronScreenSource) => {
        void dispatch(toggleSenderScreenShare(source?.id));
        closeScreenShareMenu();
    };

    const handleTabChange = (tab: TabProps): void => {
        setSelectedTab(tab.key as string);
    };

    const handleBackdropClick = (event: React.MouseEvent): void => {
        if (event.target === event.currentTarget) {
            closeScreenShareMenu();
        }
    };

    const tabs: TabProps[] = [
        {
            key: 'screens',
            label: i18next.t('videoconference.electron.screens'),
        },
        {
            key: 'windows',
            label: i18next.t('videoconference.electron.windows'),
        },
    ];

    return (
        <div
            className={twMerge(
                'bg-opacity-70 fixed inset-0 z-[300] flex flex-col items-center justify-center bg-black',
            )}
        >
            <button
                type="button"
                className={twMerge('absolute inset-0 cursor-default')}
                onClick={handleBackdropClick}
                aria-label="Close dialog"
            />
            <div
                className={twMerge(
                    'relative m-6 box-border flex max-h-[500px] w-full max-w-[600px] flex-col rounded border border-gray-600 bg-white p-6 text-black',
                )}
            >
                <Tabs
                    selectedKey={selectedTab}
                    tabs={tabs}
                    onClick={handleTabChange}
                    className={twMerge('mb-4')}
                />

                <TabPanel
                    selectedTab={selectedTab}
                    screenSources={screenSources}
                    windowSources={windowSources}
                    onStartScreenShare={startScreenShare}
                    i18next={i18next}
                />

                <div className={twMerge('flex justify-end')}>
                    <button
                        type="button"
                        className={twMerge(
                            'cursor-pointer rounded border-none bg-transparent p-3 text-sm font-light select-none hover:bg-gray-100',
                        )}
                        onClick={closeScreenShareMenu}
                    >
                        {i18next.t('cancel')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MeetingScreenSharePromptElectron;
